import { useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { Vendor, Offer } from '../lib/database.types';
import { formatMoney } from '../lib/format';

type VendorWithOffers = Vendor & { offers: Offer[]; minPriceCents: number | null };

// Manhattan center as default view.
const NYC_CENTER: [number, number] = [40.7549, -73.9840];

function priceIcon(label: string, hovered = false): L.DivIcon {
  const html = `
    <div class="ga-pin ${hovered ? 'ga-pin--hover' : ''}">
      <span>${label}</span>
    </div>`;
  return L.divIcon({
    className: 'ga-pin-wrap',
    html,
    iconSize: [60, 32],
    iconAnchor: [30, 32],
    popupAnchor: [0, -32],
  });
}

export default function VendorMap({
  vendors,
  onVendorClick,
  height = 420,
}: {
  vendors: VendorWithOffers[];
  onVendorClick?: (slug: string) => void;
  height?: number;
}) {
  const withCoords = useMemo(
    () => vendors.filter((v) => v.lat != null && v.lng != null) as (VendorWithOffers & { lat: number; lng: number })[],
    [vendors],
  );

  return (
    <div style={{ position: 'relative', width: '100%', height, borderRadius: 20, overflow: 'hidden', border: '1px solid var(--line)', boxShadow: '0 12px 36px -24px rgba(0,0,0,0.18)' }}>
      <MapContainer center={NYC_CENTER} zoom={12} scrollWheelZoom={false} style={{ width: '100%', height: '100%' }}>
        <TileLayer
          attribution='&copy; <a href="https://carto.com/attributions">CARTO</a> · &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          subdomains={['a', 'b', 'c', 'd']}
        />
        <FitToMarkers points={withCoords.map((v) => [v.lat, v.lng] as [number, number])} />
        {withCoords.map((v) => {
          const label = v.minPriceCents != null ? formatMoney(v.minPriceCents) : v.name.split(' ')[0];
          return (
            <Marker
              key={v.id}
              position={[v.lat, v.lng]}
              icon={priceIcon(label)}
              eventHandlers={{
                click: () => onVendorClick?.(v.slug),
              }}
            >
              <Popup closeButton={false}>
                <div style={{ minWidth: 200, fontFamily: 'DM Sans, system-ui, sans-serif' }}>
                  {v.hero_image_url && (
                    <div style={{ width: '100%', aspectRatio: '16 / 9', borderRadius: 10, overflow: 'hidden', background: `url(${v.hero_image_url}) center/cover no-repeat`, marginBottom: 10 }} />
                  )}
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{v.name}</div>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                    {v.neighborhood}{v.address ? ` · ${v.address}` : ''}
                  </div>
                  {v.minPriceCents != null && (
                    <div style={{ fontSize: 13, marginBottom: 8 }}>
                      From <strong>{formatMoney(v.minPriceCents)}</strong> /person
                    </div>
                  )}
                  <button
                    onClick={() => onVendorClick?.(v.slug)}
                    style={{ background: '#1c1816', color: '#fff', border: 'none', borderRadius: 999, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', width: '100%' }}
                  >View details →</button>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
      {withCoords.length === 0 && (
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none', background: 'color-mix(in oklch, var(--bg) 70%, transparent)' }}>
          <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)' }}>
            No mapped locations for these filters
          </div>
        </div>
      )}
      <style>{`
        .ga-pin-wrap { background: transparent !important; border: none !important; }
        .ga-pin {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 6px 12px;
          background: var(--bg);
          color: var(--ink);
          border: 1.5px solid var(--ink);
          border-radius: 999px;
          font-family: 'DM Sans', system-ui, sans-serif;
          font-weight: 700;
          font-size: 13px;
          letter-spacing: -0.01em;
          white-space: nowrap;
          transform-origin: center bottom;
          transition: transform 0.18s ease, background 0.18s ease, color 0.18s ease;
          box-shadow: 0 6px 14px -8px rgba(0,0,0,0.35);
          cursor: pointer;
          position: relative;
        }
        .ga-pin::after {
          content: '';
          position: absolute;
          left: 50%;
          bottom: -5px;
          transform: translateX(-50%) rotate(45deg);
          width: 8px;
          height: 8px;
          background: var(--bg);
          border-right: 1.5px solid var(--ink);
          border-bottom: 1.5px solid var(--ink);
        }
        .ga-pin:hover, .ga-pin--hover {
          background: var(--ink);
          color: var(--bg);
          transform: scale(1.08);
        }
        .ga-pin:hover::after, .ga-pin--hover::after {
          background: var(--ink);
        }
        .leaflet-popup-content-wrapper {
          border-radius: 14px !important;
          padding: 4px;
        }
        .leaflet-popup-content {
          margin: 12px 14px !important;
        }
        .leaflet-container {
          font-family: 'DM Sans', system-ui, sans-serif !important;
        }
      `}</style>
    </div>
  );
}

function FitToMarkers({ points }: { points: [number, number][] }) {
  const map = useMap();
  const prevKey = useRef<string>('');

  useEffect(() => {
    const key = points.map((p) => p.join(',')).join('|');
    if (key === prevKey.current) return;
    prevKey.current = key;

    if (points.length === 0) {
      map.setView(NYC_CENTER, 12);
      return;
    }
    if (points.length === 1) {
      map.setView(points[0], 14);
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [60, 60], maxZoom: 15 });
  }, [points, map]);

  return null;
}
